// Sprint 2 — Reorganización de configuración
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useToast } from "@/hooks/useToast";

type Pol = {
  hoursBeforeCancel: number;
  penaltyType: "lose_deposit" | "no_penalty";
  allowClientCancelReschedule: boolean;
  minHoursBeforeReschedule: number;
  maxReschedulesPerBooking: number;
  minDaysInAdvance: number;
  maxDaysInAdvance: number;
  maxBookingsPerDay: number | null;
};

const TABS = ["Límites", "Anticipaciones", "Reprogramaciones y cancelaciones"] as const;

const HOUR_OPTIONS_CANCEL = [0, 1, 2, 6, 12, 24, 48, 72, 168] as const;

export function DashboardBehavior() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<(typeof TABS)[number]>(TABS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState<Pol | null>(null);
  const [mirrorAgenda, setMirrorAgenda] = useState<string>("current");
  const [flexibleSchedule, setFlexibleSchedule] = useState(false);
  const [savingFlexible, setSavingFlexible] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [savingWaitlist, setSavingWaitlist] = useState(false);
  const [agendaColor, setAgendaColor] = useState("#6366f1");
  const [savingColor, setSavingColor] = useState(false);

  const load = useCallback(async () => {
    const [rPol, rMe] = await Promise.all([
      fetchWithSessionSync("/api/dashboard/cancellation-policy", { credentials: "include" }, refresh),
      fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh),
    ]);
    if (!rPol.ok) {
      setP(null);
      return;
    }
    const j = (await rPol.json()) as Pol;
    setP({
      ...j,
      penaltyType: j.penaltyType === "no_penalty" ? "no_penalty" : "lose_deposit",
    });
    if (rMe.ok) {
      const me = (await rMe.json()) as {
        professional?: { flexibleSchedule?: boolean; color?: string | null; waitlistEnabled?: boolean };
      };
      setFlexibleSchedule(me.professional?.flexibleSchedule ?? false);
      setAgendaColor(me.professional?.color ?? "#6366f1");
      setWaitlistEnabled(me.professional?.waitlistEnabled ?? false);
    }
  }, [refresh]);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  async function saveFlexibleSchedule(value: boolean) {
    setSavingFlexible(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flexibleSchedule: value }),
      },
      refresh,
    );
    setSavingFlexible(false);
    if (!r.ok) {
      toast.error("No se pudo guardar.");
      return;
    }
    setFlexibleSchedule(value);
    toast.success("Guardado");
  }

  async function saveWaitlistEnabled(value: boolean) {
    setSavingWaitlist(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ waitlistEnabled: value }),
      },
      refresh,
    );
    setSavingWaitlist(false);
    if (!r.ok) {
      toast.error("No se pudo guardar.");
      return;
    }
    setWaitlistEnabled(value);
    toast.success("Guardado");
  }

  async function saveAgendaColor(color: string) {
    setSavingColor(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ color }),
      },
      refresh,
    );
    setSavingColor(false);
    if (!r.ok) {
      toast.error("No se pudo guardar el color.");
      return;
    }
    toast.success("Color guardado");
  }

  async function savePatch(patch: Partial<Pol>) {
    if (!p) return;
    setSaving(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/cancellation-policy",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      },
      refresh,
    );
    setSaving(false);
    if (!r.ok) {
      toast.error("No se pudo guardar.");
      return;
    }
    const j = (await r.json()) as Pol;
    setP({ ...j, penaltyType: j.penaltyType === "no_penalty" ? "no_penalty" : "lose_deposit" });
    toast.success("Guardado");
  }

  if (loading || !p) {
    return (
      <DashboardPageShell eyebrow="Configuración" title="Comportamiento">
        <LoadingFrame variant="light" minHeight={280} />
      </DashboardPageShell>
    );
  }

  const limitEnabled = p.maxBookingsPerDay != null && p.maxBookingsPerDay > 0;

  return (
    <DashboardPageShell
      eyebrow="Configuración"
      title="Comportamiento"
      description="Límites de reservas, anticipaciones y reglas de cancelación o reprogramación."
    >
      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", mb: "6" })}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={css({
              px: "4",
              py: "2",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              border: "none",
              cursor: "pointer",
              bg: tab === t ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
              color: tab === t ? "token(colors.brand.700)" : "#64748b",
            })}
          >
            {t}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className={cx(cf.dashSurface, css({ p: { base: "4", md: "6" } }))}
        >
          {tab === "Límites" ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "5", maxW: "32rem" })}>
              <div className={css({ borderWidth: "1px", borderColor: "#e2e8f0", rounded: "lg", p: "4", bg: "#f8fafc" })}>
                <label className={css({ display: "flex", alignItems: "flex-start", gap: "3", cursor: "pointer" })}>
                  <input
                    type="checkbox"
                    checked={flexibleSchedule}
                    disabled={savingFlexible}
                    onChange={(e) => void saveFlexibleSchedule(e.target.checked)}
                    style={{ marginTop: "2px" }}
                  />
                  <div>
                    <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                      Modo solicitud (sin horario fijo)
                    </p>
                    <p className={css({ fontSize: "xs", color: "#64748b", mt: "1", lineHeight: "1.5" })}>
                      Los clientes envían una solicitud con sus datos y el servicio elegido. Vos coordinás la fecha y hora directamente. El calendario de reservas queda desactivado en tu link público.
                    </p>
                  </div>
                </label>
              </div>
              <label className={css({ display: "flex", alignItems: "center", gap: "3", fontSize: "sm", color: "#334155" })}>
                <input
                  type="checkbox"
                  checked={limitEnabled}
                  disabled={saving}
                  onChange={(e) => {
                    const on = e.target.checked;
                    void savePatch({ maxBookingsPerDay: on ? 10 : null });
                  }}
                />
                Limitar máximas reservas por día
              </label>
              <AnimatePresence initial={false}>
                {limitEnabled ? (
                  <motion.div
                    key="lim"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className={css({ overflow: "hidden" })}
                  >
                    <label className={css({ display: "block", fontSize: "sm", color: "#334155" })}>
                      <span className={css({ display: "block", mb: "2" })}>Máximo por día</span>
                      <input
                        type="number"
                        min={1}
                        max={500}
                        className={cf.input}
                        disabled={saving}
                        value={p.maxBookingsPerDay ?? 10}
                        onChange={(e) => setP({ ...p, maxBookingsPerDay: Number(e.target.value) || 1 })}
                        onBlur={() => void savePatch({ maxBookingsPerDay: p.maxBookingsPerDay })}
                      />
                    </label>
                  </motion.div>
                ) : null}
              </AnimatePresence>
              <div>
                <p className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", mb: "2" })}>
                  Color de la agenda en el calendario
                </p>
                <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
                  <input
                    type="color"
                    value={agendaColor}
                    disabled={savingColor}
                    onChange={(e) => setAgendaColor(e.target.value)}
                    onBlur={(e) => void saveAgendaColor(e.target.value)}
                    className={css({
                      w: "10",
                      h: "10",
                      rounded: "md",
                      borderWidth: "1px",
                      borderColor: "#e2e8f0",
                      cursor: "pointer",
                      p: "1",
                      bg: "white",
                    })}
                  />
                  <div
                    className={css({
                      w: "6",
                      h: "6",
                      rounded: "full",
                      flexShrink: 0,
                      borderWidth: "2px",
                      borderColor: "white",
                      boxShadow: "sm",
                    })}
                    style={{ backgroundColor: agendaColor }}
                  />
                  <span className={css({ fontSize: "xs", color: "#64748b" })}>
                    {savingColor ? "Guardando…" : "Elegí el color que identificará los eventos de esta agenda en la vista combinada."}
                  </span>
                </div>
              </div>

              <div>
                <p className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", mb: "2" })}>
                  Reflejar ocupación en otras agendas
                </p>
                <select
                  className={cf.input}
                  value={mirrorAgenda}
                  disabled
                  onChange={(e) => setMirrorAgenda(e.target.value)}
                >
                  <option value="current">Esta agenda (única disponible)</option>
                </select>
                <p className={css({ fontSize: "xs", color: "#64748b", mt: "2" })}>
                  Útil cuando tengas múltiples agendas en la misma cuenta.
                </p>
              </div>
            </div>
          ) : null}

          {tab === "Anticipaciones" ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "5", maxW: "28rem" })}>
              <label className={css({ fontSize: "sm", color: "#334155" })}>
                <span className={css({ display: "block", mb: "2", fontWeight: "medium" })}>
                  Anticipación mínima para reservar (días)
                </span>
                <input
                  type="number"
                  min={0}
                  max={365}
                  className={cf.input}
                  value={p.minDaysInAdvance}
                  onChange={(e) => setP({ ...p, minDaysInAdvance: Number(e.target.value) || 0 })}
                  onBlur={() => void savePatch({ minDaysInAdvance: p.minDaysInAdvance })}
                />
                <span className={css({ fontSize: "xs", color: "#64748b", mt: "1", display: "block" })}>
                  Con 0, los clientes pueden reservar de inmediato según tu disponibilidad.
                </span>
              </label>
              <label className={css({ fontSize: "sm", color: "#334155" })}>
                <span className={css({ display: "block", mb: "2", fontWeight: "medium" })}>
                  Anticipación máxima para reservar (días)
                </span>
                <input
                  type="number"
                  min={0}
                  max={730}
                  className={cf.input}
                  value={p.maxDaysInAdvance}
                  onChange={(e) => setP({ ...p, maxDaysInAdvance: Number(e.target.value) || 0 })}
                  onBlur={() => void savePatch({ maxDaysInAdvance: p.maxDaysInAdvance })}
                />
                <span className={css({ fontSize: "xs", color: "#64748b", mt: "1", display: "block" })}>
                  Con 30, solo podrán reservar hasta 30 días adelante.
                </span>
              </label>
            </div>
          ) : null}

          {tab === "Reprogramaciones y cancelaciones" ? (
            <div className={css({ display: "flex", flexDir: "column", gap: "5", maxW: "32rem" })}>
              <label className={css({ display: "flex", alignItems: "center", gap: "3", fontSize: "sm", color: "#334155" })}>
                <input
                  type="checkbox"
                  checked={p.allowClientCancelReschedule}
                  disabled={saving}
                  onChange={(e) => void savePatch({ allowClientCancelReschedule: e.target.checked })}
                />
                Permitir al cliente cancelar o reprogramar
              </label>
              <label className={css({ fontSize: "sm", color: "#334155" })}>
                <span className={css({ display: "block", mb: "2" })}>Horas de anticipación para reprogramar</span>
                <input
                  type="number"
                  min={0}
                  max={168}
                  className={cf.input}
                  disabled={!p.allowClientCancelReschedule}
                  value={p.minHoursBeforeReschedule}
                  onChange={(e) => setP({ ...p, minHoursBeforeReschedule: Number(e.target.value) || 0 })}
                  onBlur={() => void savePatch({ minHoursBeforeReschedule: p.minHoursBeforeReschedule })}
                />
              </label>
              <label className={css({ fontSize: "sm", color: "#334155" })}>
                <span className={css({ display: "block", mb: "2" })}>Horas de anticipación para cancelar</span>
                <select
                  className={cf.input}
                  disabled={!p.allowClientCancelReschedule}
                  value={p.hoursBeforeCancel}
                  onChange={(e) => void savePatch({ hoursBeforeCancel: Number(e.target.value) })}
                >
                  {HOUR_OPTIONS_CANCEL.map((h) => (
                    <option key={h} value={h}>
                      {h === 0 ? "Sin restricción" : `${h} ${h === 1 ? "hora" : "horas"}`}
                    </option>
                  ))}
                </select>
                <span className={css({ fontSize: "xs", color: "#64748b", mt: "1", display: "block" })}>
                  Con 0, no hay restricción de tiempo para cancelar.
                </span>
              </label>
              <label className={css({ fontSize: "sm", color: "#334155" })}>
                <span className={css({ display: "block", mb: "2" })}>Límite de reprogramaciones por reserva</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className={cf.input}
                  disabled={!p.allowClientCancelReschedule}
                  value={p.maxReschedulesPerBooking}
                  onChange={(e) => setP({ ...p, maxReschedulesPerBooking: Number(e.target.value) || 0 })}
                  onBlur={() => void savePatch({ maxReschedulesPerBooking: p.maxReschedulesPerBooking })}
                />
                <span className={css({ fontSize: "xs", color: "#64748b", mt: "1", display: "block" })}>
                  0 = sin límite.
                </span>
              </label>
              <div className={css({ borderTopWidth: "1px", borderColor: "#e2e8f0", pt: "5", mt: "2" })}>
                <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3" })}>
                  Penalidad si cancela fuera de plazo
                </p>
                <label className={css({ display: "flex", alignItems: "center", gap: "2", mb: "2", fontSize: "sm" })}>
                  <input
                    type="radio"
                    name="penalty"
                    checked={p.penaltyType === "lose_deposit"}
                    disabled={saving}
                    onChange={() => void savePatch({ penaltyType: "lose_deposit" })}
                  />
                  Pierde la seña
                </label>
                <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}>
                  <input
                    type="radio"
                    name="penalty"
                    checked={p.penaltyType === "no_penalty"}
                    disabled={saving}
                    onChange={() => void savePatch({ penaltyType: "no_penalty" })}
                  />
                  Sin penalidad (texto informativo)
                </label>
              </div>
              <div className={css({ borderWidth: "1px", borderColor: "#e2e8f0", rounded: "lg", p: "4", bg: "#f8fafc" })}>
                <label className={css({ display: "flex", alignItems: "flex-start", gap: "3", cursor: "pointer" })}>
                  <input
                    type="checkbox"
                    checked={waitlistEnabled}
                    disabled={savingWaitlist}
                    onChange={(e) => void saveWaitlistEnabled(e.target.checked)}
                    style={{ marginTop: "2px" }}
                  />
                  <div>
                    <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                      Lista de espera
                    </p>
                    <p className={css({ fontSize: "xs", color: "#64748b", mt: "1", lineHeight: "1.5" })}>
                      Cuando se cancela un turno, ofrecer automáticamente el horario a pacientes en espera.
                    </p>
                    <p className={css({ fontSize: "xs", color: "#64748b", mt: "1", lineHeight: "1.5" })}>
                      Los pacientes podrán anotarse en lista de espera desde tu página de reservas.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </DashboardPageShell>
  );
}
